import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Play, Pause, RotateCcw, Volume2, VolumeX, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ThemedBackground } from '../components/ThemedBackground';

interface AnnouncementStep {
  id: string;
  text: string;
  hasCountdown: boolean;
  countdownDuration?: number;
  isCountdownNumber?: boolean;
}

// V63: 微信环境检测
const isWechat = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('micromessenger') || ua.includes('wechat');
};

// V98: 8 套局型语音配置（删除 13 人局）
type GameType = '5-6' | '7' | '8' | '9' | '10' | '11' | '12-standard' | '12-double-lancelot';

const GAME_TYPE_OPTIONS: { value: GameType; label: string }[] = [
  { value: '5-6', label: '5-6 人局语音' },
  { value: '7', label: '7 人局语音' },
  { value: '8', label: '8 人局语音' },
  { value: '9', label: '9 人局语音' },
  { value: '10', label: '10 人局语音' },
  { value: '11', label: '11 人局语音' },
  { value: '12-standard', label: '12 人标准局语音' },
  { value: '12-double-lancelot', label: '12 人双兰局语音' },
];

// V90: 5-6 人局语音（按用户新文案替换）
const VOICE_5_6: AnnouncementStep[] = [
  { id: 'intro', text: '天黑请闭眼', hasCountdown: false },
  { id: 'evil_intro', text: '接下来进入坏人互认环节。请【莫甘娜】、【刺客】睁眼。【莫甘娜】比一，【刺客】比二', hasCountdown: false },
  { id: 'evil_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'evil_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'evil_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'evil_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'evil_close', text: '时间到，请闭眼归位。', hasCountdown: false },
  { id: 'merlin_intro', text: '接下来进入【梅林】认人环节。【梅林】睁眼，请【莫甘娜】、【刺客】举手示意。请所有需要举手的邪恶阵营玩家不要忘记举手；', hasCountdown: false },
  { id: 'merlin_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'merlin_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'merlin_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'merlin_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'merlin_close', text: '时间到，举手玩家放下手，【梅林】请闭眼。', hasCountdown: false },
  { id: 'percival_intro', text: '接下来进入【派西维尔】指认环节。【派西维尔】睁眼。请【梅林】、【莫甘娜】举手示意。', hasCountdown: false },
  { id: 'percival_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'percival_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'percival_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'percival_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'percival_close', text: '时间到，举手玩家放下手，【派西维尔】请闭眼。', hasCountdown: false },
  { id: 'outro', text: '夜间环节全部结束，天亮请睁眼。', hasCountdown: false },
];

// V89: 7 人局语音（按用户新文案替换）
const VOICE_7: AnnouncementStep[] = [
  { id: 'intro', text: '天黑请闭眼', hasCountdown: false },
  { id: 'evil_intro', text: '接下来进入邪恶阵营互认环节。', hasCountdown: false },
  { id: 'evil_wake', text: '请【莫甘娜】、【刺客】睁眼。', hasCountdown: false },
  { id: 'evil_gesture_detail', text: '【莫甘娜】比一，【刺客】比二', hasCountdown: false },
  { id: 'evil_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'evil_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'evil_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'evil_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'evil_close', text: '时间到，请闭眼归位。', hasCountdown: false },
  { id: 'merlin_intro', text: '接下来进入【梅林】认人环节。', hasCountdown: false },
  { id: 'merlin_raise_hand', text: '请【莫甘娜】、【刺客】、【奥伯伦】举手示意。请所有需要举手的邪恶阵营玩家不要忘记举手；', hasCountdown: false },
  { id: 'merlin_wake', text: '请【梅林】睁眼认人。', hasCountdown: false },
  { id: 'merlin_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'merlin_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'merlin_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'merlin_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'merlin_close', text: '时间到，举手玩家放下手，【梅林】请闭眼。', hasCountdown: false },
  { id: 'percival_intro', text: '接下来进入【派西维尔】指认环节。', hasCountdown: false },
  { id: 'percival_raise_hand', text: '请【梅林】、【莫甘娜】举手示意。', hasCountdown: false },
  { id: 'percival_wake', text: '请【派西维尔】睁眼。', hasCountdown: false },
  { id: 'percival_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'percival_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'percival_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'percival_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'percival_close', text: '时间到，举手玩家放下手，【派西维尔】请闭眼。', hasCountdown: false },
  { id: 'outro', text: '夜间环节全部结束，天亮请睁眼。', hasCountdown: false },
];

// V90: 8 人局语音（按用户新文案替换）
const VOICE_8: AnnouncementStep[] = [
  { id: 'intro', text: '天黑请闭眼', hasCountdown: false },
  { id: 'evil_intro', text: '接下来进入坏人互认环节。请【莫甘娜】、【刺客】、【爪牙】睁眼。【莫甘娜】比一，【刺客】比二，【爪牙】比三', hasCountdown: false },
  { id: 'evil_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'evil_countdown_5', text: '五', hasCountdown: true, countdownDuration: 5, isCountdownNumber: true },
  { id: 'evil_countdown_4', text: '四', hasCountdown: true, countdownDuration: 4, isCountdownNumber: true },
  { id: 'evil_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'evil_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'evil_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'evil_close', text: '时间到，请闭眼归位。', hasCountdown: false },
  { id: 'merlin_intro', text: '接下来进入【梅林】认人环节。【梅林】睁眼，请【莫甘娜】、【刺客】、【爪牙】举手示意。请所有需要举手的邪恶阵营玩家不要忘记举手；', hasCountdown: false },
  { id: 'merlin_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'merlin_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'merlin_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'merlin_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'merlin_close', text: '时间到，举手玩家放下手，【梅林】请闭眼。', hasCountdown: false },
  { id: 'percival_intro', text: '接下来进入【派西维尔】指认环节。【派西维尔】睁眼。请【梅林】、【莫甘娜】举手示意。', hasCountdown: false },
  { id: 'percival_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'percival_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'percival_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'percival_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'percival_close', text: '时间到，举手玩家放下手，【派西维尔】请闭眼。', hasCountdown: false },
  { id: 'outro', text: '夜间环节全部结束，天亮请睁眼。', hasCountdown: false },
];

// V90: 9 人局语音（按用户新文案替换）
const VOICE_9: AnnouncementStep[] = [
  { id: 'intro', text: '天黑请闭眼', hasCountdown: false },
  { id: 'evil_intro', text: '接下来进入坏人互认环节。请【莫德雷德】、【莫甘娜】、【刺客】睁眼。【莫德雷德】比一，【莫甘娜】比二，【刺客】比三', hasCountdown: false },
  { id: 'evil_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'evil_countdown_5', text: '五', hasCountdown: true, countdownDuration: 5, isCountdownNumber: true },
  { id: 'evil_countdown_4', text: '四', hasCountdown: true, countdownDuration: 4, isCountdownNumber: true },
  { id: 'evil_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'evil_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'evil_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'evil_close', text: '时间到，请闭眼归位。', hasCountdown: false },
  { id: 'merlin_intro', text: '接下来进入【梅林】认人环节。【梅林】睁眼，请【莫甘娜】、【刺客】举手示意。请所有需要举手的邪恶阵营玩家不要忘记举手；', hasCountdown: false },
  { id: 'merlin_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'merlin_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'merlin_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'merlin_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'merlin_close', text: '时间到，举手玩家放下手，【梅林】请闭眼。', hasCountdown: false },
  { id: 'percival_intro', text: '接下来进入【派西维尔】指认环节。【派西维尔】睁眼。请【梅林】、【莫甘娜】举手示意。', hasCountdown: false },
  { id: 'percival_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'percival_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'percival_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'percival_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'percival_close', text: '时间到，举手玩家放下手，【派西维尔】请闭眼。', hasCountdown: false },
  { id: 'outro', text: '夜间环节全部结束，天亮请睁眼。', hasCountdown: false },
];

// V89: 10 人局语音（按用户新文案替换）
const VOICE_10: AnnouncementStep[] = [
  { id: 'intro', text: '天黑请闭眼', hasCountdown: false },
  { id: 'evil_intro', text: '接下来进入邪恶阵营互认环节。', hasCountdown: false },
  { id: 'evil_wake', text: '请【莫德雷德】、【莫甘娜】、【刺客】睁眼。', hasCountdown: false },
  { id: 'evil_gesture_detail', text: '【莫德雷德】比一，【莫甘娜】比二，【刺客】比三', hasCountdown: false },
  { id: 'evil_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'evil_countdown_5', text: '五', hasCountdown: true, countdownDuration: 5, isCountdownNumber: true },
  { id: 'evil_countdown_4', text: '四', hasCountdown: true, countdownDuration: 4, isCountdownNumber: true },
  { id: 'evil_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'evil_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'evil_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'evil_close', text: '时间到，请闭眼归位。', hasCountdown: false },
  { id: 'merlin_intro', text: '接下来进入【梅林】认人环节。', hasCountdown: false },
  { id: 'merlin_raise_hand', text: '请【莫甘娜】、【刺客】、【奥伯伦】举手示意。请所有需要举手的邪恶阵营玩家不要忘记举手；', hasCountdown: false },
  { id: 'merlin_wake', text: '请【梅林】睁眼认人。', hasCountdown: false },
  { id: 'merlin_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'merlin_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'merlin_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'merlin_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'merlin_close', text: '时间到，举手玩家放下手，【梅林】请闭眼。', hasCountdown: false },
  { id: 'percival_intro', text: '接下来进入【派西维尔】指认环节。', hasCountdown: false },
  { id: 'percival_raise_hand', text: '请【梅林】、【莫甘娜】举手示意。', hasCountdown: false },
  { id: 'percival_wake', text: '请【派西维尔】睁眼。', hasCountdown: false },
  { id: 'percival_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'percival_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'percival_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'percival_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'percival_close', text: '时间到，举手玩家放下手，【派西维尔】请闭眼。', hasCountdown: false },
  { id: 'outro', text: '夜间环节全部结束，天亮请睁眼。', hasCountdown: false },
];

// V89: 11 人局语音（按用户新文案替换）
const VOICE_11: AnnouncementStep[] = [
  { id: 'intro', text: '天黑请闭眼', hasCountdown: false },
  { id: 'evil_intro', text: '接下来进入邪恶阵营互认环节。', hasCountdown: false },
  { id: 'evil_wake', text: '请【莫德雷德】、【莫甘娜】、【刺客】、【爪牙】睁眼。', hasCountdown: false },
  { id: 'evil_gesture_detail', text: '【莫德雷德】比一，【莫甘娜】比二，【刺客】比三、【爪牙】比四', hasCountdown: false },
  { id: 'evil_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'evil_countdown_5', text: '五', hasCountdown: true, countdownDuration: 5, isCountdownNumber: true },
  { id: 'evil_countdown_4', text: '四', hasCountdown: true, countdownDuration: 4, isCountdownNumber: true },
  { id: 'evil_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'evil_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'evil_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'evil_close', text: '时间到，请闭眼归位。', hasCountdown: false },
  { id: 'merlin_intro', text: '接下来进入【梅林】认人环节。', hasCountdown: false },
  { id: 'merlin_raise_hand', text: '请【莫甘娜】、【刺客】、【爪牙】举手示意。请所有需要举手的邪恶阵营玩家不要忘记举手；', hasCountdown: false },
  { id: 'merlin_wake', text: '请【梅林】睁眼认人。', hasCountdown: false },
  { id: 'merlin_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'merlin_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'merlin_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'merlin_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'merlin_close', text: '时间到，举手玩家放下手，【梅林】请闭眼。', hasCountdown: false },
  { id: 'percival_intro', text: '接下来进入【派西维尔】指认环节。', hasCountdown: false },
  { id: 'percival_raise_hand', text: '请【梅林】、【莫甘娜】举手示意。', hasCountdown: false },
  { id: 'percival_wake', text: '请【派西维尔】睁眼。', hasCountdown: false },
  { id: 'percival_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'percival_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'percival_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'percival_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'percival_close', text: '时间到，举手玩家放下手，【派西维尔】请闭眼。', hasCountdown: false },
  { id: 'outro', text: '夜间环节全部结束，天亮请睁眼。', hasCountdown: false },
];

// V89: 12 人标准局语音（按用户新文案替换）
const VOICE_12_STANDARD: AnnouncementStep[] = [
  { id: 'intro', text: '天黑请闭眼', hasCountdown: false },
  { id: 'evil_intro', text: '接下来进入邪恶阵营互认环节。', hasCountdown: false },
  { id: 'evil_wake', text: '请【莫德雷德】、【莫甘娜】、【刺客】睁眼。', hasCountdown: false },
  { id: 'evil_gesture_detail', text: '【莫德雷德】比一，【莫甘娜】比二，【刺客】比三', hasCountdown: false },
  { id: 'evil_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'evil_countdown_5', text: '五', hasCountdown: true, countdownDuration: 5, isCountdownNumber: true },
  { id: 'evil_countdown_4', text: '四', hasCountdown: true, countdownDuration: 4, isCountdownNumber: true },
  { id: 'evil_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'evil_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'evil_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'evil_close', text: '时间到，请闭眼归位。', hasCountdown: false },
  { id: 'merlin_intro', text: '接下来进入【梅林】认人环节。', hasCountdown: false },
  { id: 'merlin_raise_hand', text: '请【莫甘娜】、【刺客】、两位【奥伯伦】举手示意。请所有需要举手的邪恶阵营玩家不要忘记举手；', hasCountdown: false },
  { id: 'merlin_wake', text: '请【梅林】睁眼认人。', hasCountdown: false },
  { id: 'merlin_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'merlin_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'merlin_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'merlin_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'merlin_close', text: '时间到，举手玩家放下手，【梅林】请闭眼。', hasCountdown: false },
  { id: 'percival_intro', text: '接下来进入【派西维尔】指认环节。', hasCountdown: false },
  { id: 'percival_raise_hand', text: '请【梅林】、【莫甘娜】举手示意。', hasCountdown: false },
  { id: 'percival_wake', text: '请【派西维尔】睁眼。', hasCountdown: false },
  { id: 'percival_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'percival_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'percival_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'percival_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'percival_close', text: '时间到，举手玩家放下手，【派西维尔】请闭眼。', hasCountdown: false },
  { id: 'outro', text: '夜间环节全部结束，天亮请睁眼。', hasCountdown: false },
];

// V89: 12 人双兰局语音（按用户新文案替换）
const LANCELOT_ANNOUNCEMENTS: AnnouncementStep[] = [
  { id: 'intro', text: '天黑请闭眼', hasCountdown: false },
  { id: 'evil_intro', text: '接下来进入邪恶阵营互认环节。', hasCountdown: false },
  { id: 'evil_wake', text: '请【莫德雷德】、【莫甘娜】、【刺客】睁眼。', hasCountdown: false },
  { id: 'evil_gesture_detail', text: '【莫德雷德】比一，【莫甘娜】比二，【刺客】比三', hasCountdown: false },
  { id: 'evil_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'evil_countdown_5', text: '五', hasCountdown: true, countdownDuration: 5, isCountdownNumber: true },
  { id: 'evil_countdown_4', text: '四', hasCountdown: true, countdownDuration: 4, isCountdownNumber: true },
  { id: 'evil_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'evil_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'evil_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'evil_close', text: '时间到，请闭眼归位。', hasCountdown: false },
  { id: 'merlin_intro', text: '接下来进入【梅林】认人环节。', hasCountdown: false },
  { id: 'merlin_raise_hand', text: '请【莫甘娜】、【刺客】、【坏兰斯洛特】、【奥伯伦】举手示意。请所有需要举手的邪恶阵营玩家不要忘记举手；', hasCountdown: false },
  { id: 'merlin_wake', text: '请【梅林】睁眼认人。', hasCountdown: false },
  { id: 'merlin_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'merlin_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'merlin_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'merlin_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'merlin_close', text: '时间到，举手玩家放下手，【梅林】请闭眼。', hasCountdown: false },
  { id: 'percival_intro', text: '接下来进入【派西维尔】指认环节。', hasCountdown: false },
  { id: 'percival_raise_hand', text: '请【梅林】、【莫甘娜】举手示意。', hasCountdown: false },
  { id: 'percival_wake', text: '请【派西维尔】睁眼。', hasCountdown: false },
  { id: 'percival_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'percival_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'percival_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'percival_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'percival_close', text: '时间到，举手玩家放下手，【派西维尔】请闭眼。', hasCountdown: false },
  { id: 'lancelot_intro', text: '接下来进入【兰斯洛特】互认环节。', hasCountdown: false },
  { id: 'lancelot_wake', text: '请两位【兰斯洛特】睁眼举手', hasCountdown: false },
  { id: 'lancelot_countdown_title', text: '倒计时：', hasCountdown: false },
  { id: 'lancelot_countdown_3', text: '三', hasCountdown: true, countdownDuration: 3, isCountdownNumber: true },
  { id: 'lancelot_countdown_2', text: '二', hasCountdown: true, countdownDuration: 2, isCountdownNumber: true },
  { id: 'lancelot_countdown_1', text: '一', hasCountdown: true, countdownDuration: 1, isCountdownNumber: true },
  { id: 'lancelot_close', text: '请两位【兰斯洛特】闭眼收手', hasCountdown: false },
  { id: 'outro', text: '夜间环节全部结束，天亮请睁眼。', hasCountdown: false },
];

// V90: 根据局型获取对应的播报文案
const getAnnouncementsByGameType = (gameType: GameType): AnnouncementStep[] => {
  switch (gameType) {
    case '5-6':
      return VOICE_5_6;
    case '7':
      return VOICE_7;
    case '8':
      return VOICE_8;
    case '9':
      return VOICE_9;
    case '10':
      return VOICE_10;
    case '11':
      return VOICE_11;
    case '12-standard':
      return VOICE_12_STANDARD;
    case '12-double-lancelot':
      return LANCELOT_ANNOUNCEMENTS;
    default:
      return VOICE_5_6;
  }
};

// 需要加重语气的角色名称
const EMPHASIZED_ROLES = ['梅林', '莫甘娜', '派西维尔', '莫德雷德', '刺客', '爪牙', '奥伯伦', '好兰斯洛特', '坏兰斯洛特', '兰斯洛特'];

// 为角色名称添加重音标记
const emphasizeRoles = (text: string): string => {
  let result = text;
  EMPHASIZED_ROLES.forEach(role => {
    result = result.replace(new RegExp(role, 'g'), `，${role}，`);
  });
  return result;
};

// V65: 语音播放器类 - 优化延迟和并发问题
class VoicePlayer {
  private synth: SpeechSynthesis | null = null;
  private audio: HTMLAudioElement | null = null;
  private isWechat: boolean;
  private audioContext: AudioContext | null = null;
  private currentResolve: (() => void) | null = null;
  private isPlaying = false;
  private abortController: AbortController | null = null;
  private timeoutId: number | null = null;

  constructor() {
    this.isWechat = isWechat();
    
    // 初始化 SpeechSynthesis
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synth = window.speechSynthesis;
    }
    
    // 初始化 AudioContext（用于解锁音频）
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        this.audioContext = new AudioContextClass();
      }
    } catch (e) {
      console.log('AudioContext 初始化失败:', e);
    }
  }

  // V65: 检查是否正在播放
  isCurrentlyPlaying(): boolean {
    return this.isPlaying;
  }

  // V63: 解锁音频（微信环境必需）
  async unlockAudio(): Promise<void> {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    
    // 播放一个无声的音频来解锁
    try {
      const silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=');
      silentAudio.volume = 0.01;
      await silentAudio.play();
    } catch (e) {
      // 忽略错误
    }
  }

  // V65: 带超时的 Promise 包装
  private withTimeout<T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(errorMessage));
      }, timeoutMs);
      
      promise.then(
        (result) => {
          clearTimeout(timeoutId);
          resolve(result);
        },
        (error) => {
          clearTimeout(timeoutId);
          reject(error);
        }
      );
    });
  }

  // V69: 缓存固定音色以确保语音一致性
  private cachedVoice: SpeechSynthesisVoice | null = null;

  // V69: 获取固定音色（确保语音一致性）
  private getFixedVoice(): SpeechSynthesisVoice | null {
    if (this.cachedVoice) {
      return this.cachedVoice;
    }

    if (!this.synth) {
      return null;
    }

    const voices = this.synth.getVoices();
    // V69: 优先选择中文语音，确保音色一致
    const zhVoice = voices.find(v => 
      v.lang.startsWith('zh') || 
      v.name.includes('中文') || 
      v.name.includes('Chinese')
    );
    
    // 如果没有中文语音，选择第一个可用语音（确保一致性）
    this.cachedVoice = zhVoice || voices[0] || null;
    return this.cachedVoice;
  }

  // V92: 修复"五"字语音异常 - 替换为"伍"
  private fixWuCharacter(text: string): string {
    return text.replace(/五/g, '伍');
  }

  // V63: 使用 SpeechSynthesis 播放（V65优化，V69: 固定音色，V92: 修复"五"字）
  private async speakWithTTS(text: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.synth) {
        reject(new Error('SpeechSynthesis 不支持'));
        return;
      }

      // 取消之前的播放
      this.synth.cancel();

      // V92: 修复"五"字语音异常
      const fixedText = this.fixWuCharacter(text);

      const utterance = new SpeechSynthesisUtterance(fixedText);
      utterance.lang = 'zh-CN';
      utterance.rate = 0.9; // V65: 稍微加快语速减少延迟感
      utterance.pitch = 1;

      // V69: 使用固定音色确保语音一致性
      const fixedVoice = this.getFixedVoice();
      if (fixedVoice) {
        utterance.voice = fixedVoice;
      }

      utterance.onend = () => {
        this.isPlaying = false;
        resolve();
      };

      utterance.onerror = (e) => {
        console.log('TTS 播放错误:', e);
        this.isPlaying = false;
        // 错误时也 resolve，避免卡住
        resolve();
      };

      this.currentResolve = resolve;
      this.isPlaying = true;

      // V65: 非微信环境直接播放，减少延迟
      this.synth.speak(utterance);
    });
  }

  // V65: 使用 Audio 播放（优化版）
  private async speakWithAudio(text: string): Promise<void> {
    // 先停止之前的音频
    this.stopAudio();
    
    return new Promise((resolve, reject) => {
      // 使用百度语音合成 API 生成音频
      const encodedText = encodeURIComponent(text);
      const audioUrl = `https://fanyi.baidu.com/gettts?lan=zh&text=${encodedText}&spd=5&source=web`; // V65: 加快速度参数
      
      this.audio = new Audio(audioUrl);
      this.isPlaying = true;
      
      // V65: 预加载音频
      this.audio.preload = 'auto';
      
      this.audio.onended = () => {
        this.isPlaying = false;
        resolve();
      };
      
      this.audio.onerror = (e) => {
        console.log('Audio 播放错误:', e);
        this.isPlaying = false;
        reject(new Error('音频播放失败'));
      };
      
      // V65: 添加加载超时处理
      const loadTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.isPlaying = false;
          reject(new Error('音频加载超时'));
        }
      }, 8000); // 8秒加载超时
      
      this.audio.oncanplaythrough = () => {
        clearTimeout(loadTimeout);
      };
      
      this.currentResolve = resolve;
      
      // 播放音频
      this.audio.play().catch((err) => {
        clearTimeout(loadTimeout);
        this.isPlaying = false;
        reject(err);
      });
    });
  }

  // V65: 停止音频播放
  private stopAudio(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.audio.src = '';
      this.audio = null;
    }
  }

  // V65: 主播放方法 - 优化延迟和并发
  async speak(text: string): Promise<void> {
    if (!text) return;

    // V65: 停止当前播放，确保单例模式
    this.stop();

    // 先解锁音频
    await this.unlockAudio();

    // V65: 创建新的 AbortController
    this.abortController = new AbortController();

    // V65: 使用 Promise.race 实现超时和竞争
    const speakPromise = this.isWechat 
      ? this.speakWithAudio(text).catch(() => this.speakWithTTS(text))
      : this.speakWithTTS(text).catch(() => this.speakWithAudio(text));

    try {
      // V65: 10秒超时
      await this.withTimeout(speakPromise, 10000, '语音播放超时');
    } catch (error) {
      console.log('语音播放失败:', error);
      this.isPlaying = false;
      throw error;
    }
  }

  // V63: 播放数字（倒计时用，V65优化，V92: 修复"五"字）
  async speakNumber(num: number): Promise<void> {
    // V92: 使用"伍"替代"五"避免语音异常
    const numberText = ['零', '一', '二', '三', '四', '伍', '六', '七', '八', '九', '十'][num] || String(num);
    await this.speak(numberText);
  }

  // V65: 停止播放（优化版）
  stop(): void {
    this.isPlaying = false;
    
    if (this.synth) {
      this.synth.cancel();
    }
    
    this.stopAudio();
    
    if (this.currentResolve) {
      this.currentResolve();
      this.currentResolve = null;
    }
    
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  // V92: 修复暂停功能 - 添加暂停状态追踪
  private isPaused = false;

  // V92: 修复暂停功能
  pause(): void {
    this.isPaused = true;
    if (this.synth) {
      this.synth.pause();
    }
    if (this.audio) {
      this.audio.pause();
    }
  }

  // V92: 修复恢复功能
  resume(): void {
    this.isPaused = false;
    if (this.synth) {
      this.synth.resume();
    }
    if (this.audio) {
      this.audio.play().catch(() => {});
    }
  }

  // V92: 检查是否处于暂停状态
  isCurrentlyPaused(): boolean {
    return this.isPaused;
  }
}

export default function VoiceHost() {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isCountingDown, setIsCountingDown] = useState(false);
  // V89: 默认选中 5-6 人局语音
  const [selectedGameType, setSelectedGameType] = useState<GameType>('5-6');
  // V50: 局型选择区块展开/收起状态
  const [isGameTypeExpanded, setIsGameTypeExpanded] = useState(true);
  // V63: 音频初始化状态
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [isWechatEnv, setIsWechatEnv] = useState(false);
  // V65: 加载状态
  const [isLoadingVoice, setIsLoadingVoice] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const voicePlayerRef = useRef<VoicePlayer | null>(null);
  const isProcessingRef = useRef(false);
  // V93: 添加暂停状态 ref 用于在异步流程中检查
  const isPausedRef = useRef(false);

  // V63: 初始化语音播放器
  useEffect(() => {
    const wechat = isWechat();
    setIsWechatEnv(wechat);
    
    // 创建语音播放器实例
    voicePlayerRef.current = new VoicePlayer();
    
    return () => {
      voicePlayerRef.current?.stop();
    };
  }, []);

  // V89: 根据选择的局型确定播报文案
  const currentAnnouncements = getAnnouncementsByGameType(selectedGameType);

  // V89: 处理局型选择（互斥逻辑）
  // V93: 切换局型时彻底重置状态
  const handleGameTypeChange = (value: GameType) => {
    // 先停止当前播放
    stopVoice();
    // V93: 重置暂停状态
    isPausedRef.current = false;
    // V93: 重置处理状态
    isProcessingRef.current = false;
    // 直接设置为新值（互斥，总是切换）
    setSelectedGameType(value);
    // 重置播报状态
    setCurrentStepIndex(0);
    setIsPlaying(false);
    setIsPaused(false);
    setCountdown(0);
    setIsComplete(false);
    setIsCountingDown(false);
  };

  // V50: 开始播报后自动收起局型选择区块
  const startAnnouncementWithCollapse = async () => {
    setIsGameTypeExpanded(false);
    await initializeAudio();
    startAnnouncement();
  };

  // V63: 初始化音频（微信环境必需）
  const initializeAudio = useCallback(async () => {
    if (audioInitialized || !voicePlayerRef.current) return;
    
    try {
      await voicePlayerRef.current.unlockAudio();
      setAudioInitialized(true);
      console.log('音频初始化成功');
    } catch (error) {
      console.log('音频初始化失败:', error);
    }
  }, [audioInitialized]);

  // V65: 播放语音（带加载状态）
  const speak = useCallback(async (text: string): Promise<void> => {
    if (!soundEnabled || !voicePlayerRef.current) return;
    
    // 确保音频已初始化
    if (!audioInitialized) {
      await initializeAudio();
    }
    
    setIsLoadingVoice(true);
    setVoiceError(null);
    
    try {
      await voicePlayerRef.current.speak(text);
    } catch (error) {
      console.error('语音播放失败:', error);
      setVoiceError('语音加载失败，请重试');
      // 3秒后自动清除错误提示
      setTimeout(() => setVoiceError(null), 3000);
    } finally {
      setIsLoadingVoice(false);
    }
  }, [soundEnabled, audioInitialized, initializeAudio]);

  // V63: 播报单个数字（倒计时用）
  const speakNumber = useCallback(async (num: number): Promise<void> => {
    if (!soundEnabled || !voicePlayerRef.current) return;
    await voicePlayerRef.current.speakNumber(num);
  }, [soundEnabled]);

  // V93: 修复 processStep 中的暂停逻辑 - 添加暂停检查
  const processStep = useCallback(async (stepIndex: number) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    const step = currentAnnouncements[stepIndex];
    if (!step) {
      setIsComplete(true);
      setIsPlaying(false);
      isProcessingRef.current = false;
      return;
    }

    setCurrentStepIndex(stepIndex);

    // V93: 检查是否已暂停，如果暂停则停止处理
    if (isPausedRef.current) {
      isProcessingRef.current = false;
      return;
    }

    // 如果是倒计时数字，特殊处理
    if (step.isCountdownNumber && step.countdownDuration) {
      setCountdown(step.countdownDuration);
      setIsCountingDown(true);
      await speakNumber(step.countdownDuration);
      // 数字播报后等待1秒（除了最后一个数字）
      if (stepIndex < currentAnnouncements.length - 1 && currentAnnouncements[stepIndex + 1].isCountdownNumber) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      isProcessingRef.current = false;
      processStep(stepIndex + 1);
      return;
    }

    setIsCountingDown(false);
    const emphasizedText = emphasizeRoles(step.text);

    await speak(emphasizedText);

    // V93: 检查是否已暂停，如果暂停则停止处理
    if (isPausedRef.current) {
      isProcessingRef.current = false;
      return;
    }

    // 普通文案播报后等待1.5秒
    await new Promise(resolve => setTimeout(resolve, 1500));
    isProcessingRef.current = false;
    processStep(stepIndex + 1);
  }, [speak, speakNumber, currentAnnouncements]);

  const startAnnouncement = async () => {
    // V65: 如果正在加载，阻止重复点击
    if (isLoadingVoice) return;
    
    // V63: 首次播放需要初始化
    if (!audioInitialized) {
      await initializeAudio();
    }
    
    setIsPlaying(true);
    setIsPaused(false);
    setIsComplete(false);
    setCountdown(0);
    setIsCountingDown(false);
    setCurrentStepIndex(0);
    isProcessingRef.current = false;
    processStep(0);
  };

  // V93: 修复暂停按钮功能 - 确保语音真正停止并记录暂停状态
  const pauseAnnouncement = useCallback(() => {
    if (!voicePlayerRef.current) return;

    const newPausedState = !isPaused;
    setIsPaused(newPausedState);
    // V93: 同步更新 ref
    isPausedRef.current = newPausedState;

    if (newPausedState) {
      // 暂停 - V93: 同时停止语音播放
      voicePlayerRef.current.stop();
      // V93: 停止倒计时
      setIsCountingDown(false);
    } else {
      // 恢复 - V93: 从当前步骤重新开始
      isProcessingRef.current = false;
      processStep(currentStepIndex);
    }
  }, [isPaused, currentStepIndex, processStep]);

  // V71: 停止语音播放的辅助函数
  const stopVoice = useCallback(() => {
    if (voicePlayerRef.current) {
      voicePlayerRef.current.stop();
    }
    isProcessingRef.current = false;
  }, []);

  // V93: 处理返回首页 - 彻底停止语音并重置状态
  const handleNavigateHome = useCallback(() => {
    // V93: 先停止语音播放
    stopVoice();
    // V93: 重置暂停状态
    isPausedRef.current = false;
    // V93: 彻底重置播放状态
    setIsPlaying(false);
    setIsPaused(false);
    setIsCountingDown(false);
    setCountdown(0);
    // V93: 重置处理状态
    isProcessingRef.current = false;
    navigate('/');
  }, [navigate, stopVoice]);

  // V93: 优化重置按钮 - 彻底停止语音并重置所有状态
  const resetAnnouncement = useCallback(() => {
    // V93: 先停止语音播放
    stopVoice();
    // V93: 重置暂停状态
    isPausedRef.current = false;
    setIsPaused(false);
    // V93: 重置处理状态
    isProcessingRef.current = false;
    // V93: 重置所有状态
    setCurrentStepIndex(0);
    setIsPlaying(false);
    setCountdown(0);
    setIsComplete(false);
    setIsCountingDown(false);
  }, [stopVoice]);

  // V92: 移除 nextStep 和 prevStep 函数

  // 获取当前环节信息
  const getCurrentPhase = () => {
    if (currentStepIndex === 0) return '开场';
    
    // V90: 根据当前选择的局型判断流程环节（新文案合并了步骤）
    if (selectedGameType === '12-double-lancelot') {
      // 12 人双兰局索引（新文案：0开场,1坏人互认,2-6倒计时,7闭眼,8梅林认人,9-13倒计时,14闭眼,15派西维尔,16-20倒计时,21闭眼,22兰斯洛特互认,23-27倒计时,28闭眼,29结束）
      if (currentStepIndex >= 1 && currentStepIndex <= 7) return '坏人互认环节';
      if (currentStepIndex >= 8 && currentStepIndex <= 14) return '梅林认人环节';
      if (currentStepIndex >= 15 && currentStepIndex <= 21) return '派西维尔指认环节';
      if (currentStepIndex >= 22 && currentStepIndex <= 28) return '兰斯洛特互认环节';
    } else {
      // 其他局型索引（新文案：0开场,1坏人互认,2-6/7倒计时,7/8闭眼,8/9梅林认人,10-14/15倒计时,15/16闭眼,17/18派西维尔,19-23/24倒计时,24/25闭眼,25/26结束）
      if (currentStepIndex >= 1 && currentStepIndex <= 7) return '坏人互认环节';
      if (currentStepIndex >= 8 && currentStepIndex <= 15) return '梅林认人环节';
      if (currentStepIndex >= 16 && currentStepIndex <= 24) return '派西维尔指认环节';
    }
    return '夜间环节结束';
  };

  const currentStep = currentAnnouncements[currentStepIndex];
  const progress = isComplete 
    ? 100 
    : ((currentStepIndex) / currentAnnouncements.length) * 100;

  // 获取流程概览数据
  const getPhaseOverview = () => {
    if (selectedGameType === '12-double-lancelot') {
      return [
        { id: 1, name: '邪恶阵营互认环节', desc: '莫德雷德、莫甘娜、刺客互认身份（5秒倒计时）', start: 1, end: 11 },
        { id: 2, name: '梅林认人环节', desc: '梅林识别邪恶阵营玩家（3秒倒计时）', start: 12, end: 19 },
        { id: 3, name: '派西维尔指认环节', desc: '派西维尔分辨梅林和莫甘娜（3秒倒计时）', start: 20, end: 26 },
        { id: 4, name: '兰斯洛特互认环节', desc: '好兰斯洛特和坏兰斯洛特互认（3秒倒计时）', start: 27, end: 32 },
      ];
    }
    return [
      { id: 1, name: '邪恶阵营互认环节', desc: '邪恶阵营互认身份（3/5秒倒计时）', start: 1, end: 8 },
      { id: 2, name: '梅林认人环节', desc: '梅林识别邪恶阵营玩家（3秒倒计时）', start: 9, end: 16 },
      { id: 3, name: '派西维尔指认环节', desc: '派西维尔分辨梅林和莫甘娜（3秒倒计时）', start: 17, end: 23 },
    ];
  };

  const phases = getPhaseOverview();

  return (
    <ThemedBackground pageName="voice-host">
    <div className="min-h-screen p-5 pt-[72px] pb-8">
      {/* V189: 固定头部 */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center gap-4 px-5 py-3" style={{ backgroundColor: '#1A0F2A', borderBottom: '1px solid #5A4BFF35' }}>
        <button
          onClick={handleNavigateHome}
          className="w-10 h-10 rounded-lg bg-avalon-card flex items-center justify-center text-white hover:bg-avalon-purple transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex flex-col">
          <h1 className="text-xl font-bold text-white">语音主持</h1>
          <span className="text-xs text-avalon-text/70">（谷歌浏览器可正常打开）</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            disabled={isLoadingVoice}
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
              soundEnabled ? 'bg-avalon-gold text-avalon-bg-dark' : 'bg-avalon-card text-avalon-text'
            } ${isLoadingVoice ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isLoadingVoice ? (
              <div className="w-5 h-5 border-2 border-avalon-bg-dark border-t-transparent rounded-full animate-spin" />
            ) : soundEnabled ? (
              <Volume2 className="w-5 h-5" />
            ) : (
              <VolumeX className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* V65: 语音加载状态提示 */}
      {isLoadingVoice && (
        <div className="mb-4 p-3 rounded-xl bg-avalon-purple/30 border border-avalon-purple/50 flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-avalon-gold border-t-transparent rounded-full animate-spin" />
          <span className="text-avalon-gold text-sm">正在加载语音...</span>
        </div>
      )}

      {/* V65: 语音错误提示 */}
      {voiceError && (
        <div className="mb-4 p-3 rounded-xl bg-red-500/20 border border-red-500/50 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400 text-sm">{voiceError}</span>
        </div>
      )}

      {/* V63: 微信环境音频初始化提示 */}
      {isWechatEnv && !audioInitialized && (
        <div 
          onClick={initializeAudio}
          className="mb-6 p-4 rounded-xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/50 cursor-pointer hover:bg-amber-500/30 transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/30 flex items-center justify-center flex-shrink-0">
              <Volume2 className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-amber-300 font-semibold text-sm mb-1">点击启用语音播放</h3>
              <p className="text-avalon-text text-xs leading-relaxed">
                微信环境需要点击页面启用音频播放权限。点击此区域或下方"开始完整播报"按钮即可开始。
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="mb-6">
        <div className="w-full h-2 bg-avalon-purple rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-avalon-gold to-yellow-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-avalon-text">
          <span>步骤 {currentStepIndex + 1} / {currentAnnouncements.length}</span>
          <span>{Math.round(progress)}%</span>
        </div>
      </div>

      {/* V50: 局型选择区块（可伸缩） */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <p className="text-avalon-text text-sm">选择局型语音（互斥选择）：</p>
          <button
            onClick={() => setIsGameTypeExpanded(!isGameTypeExpanded)}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-avalon-card text-avalon-text hover:text-white hover:bg-avalon-purple transition-colors text-xs"
          >
            {isGameTypeExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                收起
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                展开
              </>
            )}
          </button>
        </div>
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isGameTypeExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="grid grid-cols-2 gap-3">
            {GAME_TYPE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedGameType === option.value
                    ? 'border-avalon-gold bg-avalon-gold/10'
                    : 'border-avalon-purple/50 bg-avalon-card hover:border-avalon-purple'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedGameType === option.value}
                  onChange={() => handleGameTypeChange(option.value)}
                  className="w-4 h-4 rounded border-2 border-avalon-purple bg-avalon-bg-dark text-avalon-gold focus:ring-avalon-gold focus:ring-2 cursor-pointer"
                />
                <span className={`text-sm ${selectedGameType === option.value ? 'text-avalon-gold' : 'text-white'}`}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>
        {!isGameTypeExpanded && (
          <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-avalon-card/50">
            <span className="text-avalon-gold text-sm">
              当前使用：{GAME_TYPE_OPTIONS.find(opt => opt.value === selectedGameType)?.label}
            </span>
          </div>
        )}
        {isGameTypeExpanded && (
          <p className="text-avalon-gold text-xs mt-2">
            当前使用：{GAME_TYPE_OPTIONS.find(opt => opt.value === selectedGameType)?.label}
          </p>
        )}
      </div>

      {isComplete ? (
        <div className="avalon-card-lg text-center py-12">
          <div className="text-6xl mb-4">🌅</div>
          <h2 className="text-2xl font-bold text-white mb-2">天亮了</h2>
          <p className="text-avalon-text mb-6">夜间流程已全部完成</p>
          <button
            onClick={resetAnnouncement}
            className="avalon-btn px-8"
          >
            重新开始
          </button>
        </div>
      ) : (
        <>
          {/* Current Step Card */}
          <div className="avalon-card-lg mb-6">
            {/* 当前环节标识 */}
            <div className="text-center mb-4">
              <span className="inline-block px-4 py-1 rounded-full bg-avalon-purple text-avalon-gold text-sm font-medium">
                {getCurrentPhase()}
              </span>
            </div>

            {/* 播报内容 */}
            <div className="text-center mb-6">
              {isCountingDown ? (
                <div className="text-7xl font-bold text-avalon-gold text-shadow-gold">
                  {countdown}
                </div>
              ) : (
                <p className="text-white text-lg leading-relaxed">
                  {currentStep?.text}
                </p>
              )}
            </div>

            {/* Control Buttons */}
            <div className="flex gap-3">
              {!isPlaying ? (
                <button
                  onClick={startAnnouncementWithCollapse}
                  className="flex-1 avalon-btn flex items-center justify-center gap-2 py-4"
                >
                  <Play className="w-5 h-5" />
                  开始完整播报
                </button>
              ) : (
                <button
                  onClick={pauseAnnouncement}
                  className="flex-1 avalon-btn avalon-btn-secondary flex items-center justify-center gap-2 py-4"
                >
                  {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                  {isPaused ? '继续' : '暂停'}
                </button>
              )}
              <button
                onClick={resetAnnouncement}
                className="avalon-btn avalon-btn-secondary px-6 flex items-center justify-center"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* V92: 移除上一步/下一步按钮区域 */}

          {/* Phase Overview */}
          <div className="mt-6">
            <h3 className="text-white font-semibold mb-4">流程概览</h3>
            <div className="space-y-3">
              {phases.map((phase) => (
                <div
                  key={phase.id}
                  className={`p-4 rounded-xl transition-all ${
                    currentStepIndex >= phase.start && currentStepIndex <= phase.end
                      ? 'bg-avalon-gold/20 border border-avalon-gold/50'
                      : currentStepIndex > phase.end
                        ? 'bg-avalon-purple/30'
                        : 'bg-avalon-card opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        currentStepIndex >= phase.start && currentStepIndex <= phase.end
                          ? 'bg-avalon-gold text-avalon-bg-dark'
                          : 'bg-avalon-purple text-avalon-text'
                      }`}
                    >
                      {phase.id}
                    </span>
                    <span className="text-white font-medium">{phase.name}</span>
                    {currentStepIndex >= phase.start && currentStepIndex <= phase.end && (
                      <span className="text-avalon-gold text-xs">进行中</span>
                    )}
                  </div>
                  <p className="text-avalon-text text-sm pl-9">{phase.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  </ThemedBackground>
  );
}
